import React from "react";

import { Theme, useTheme } from "@mui/material/styles";

import {
  logDebug,
  mergeProperties,
  packableDeepClone,
} from "packable-components";
import { OrderActionsProps, ShippingStatus } from "./Order.types";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import OrderStatus from "./OrderStatus";

const useStyles = (theme: Theme) => ({
  OrderActionContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center",
    alignItems: "center",
  },
  OrderActionSingleItemTopPaddingRowContainer: {
    pt: 2,
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  OrderActionSingleItemRowContainer: {
    width: "512px",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  OrderActionContentFullWidthTextFieldContainer: {
    width: "100%",
    padding: 1,
  },
  OrderActionContentFullWidthTextField: {
    width: "100%",
  },
  PackableSalectableChip: {
    muiSize: "small",
  },
});

export const OrderCancellationRequestedActions: React.FC<OrderActionsProps> = (
  props
): React.JSX.Element => {
  const { orderItemAction, onOrderItemActionUpdate, styles } = props;

  logDebug("OrderCancellationRequestedActions PROPS");
  logDebug(props);

  const theme = useTheme();

  const defaultStyles = useStyles(theme);
  const effectiveStyles =
    styles === undefined
      ? defaultStyles
      : mergeProperties(defaultStyles, styles);

  logDebug("OrderCancellationRequestedActions effectiveStyles");
  logDebug(effectiveStyles);

  const handleTrackingNumberValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.trackingNumber = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleNoteValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.note = event.target.value;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleIssueRefundValueChange = (event: any) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.issueRefund = !newOrderItemAction.issueRefund;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    selection: ShippingStatus
  ) => {
    let newOrderItemAction = packableDeepClone(orderItemAction);
    newOrderItemAction.shippingStatus = selection;
    onOrderItemActionUpdate(newOrderItemAction);
  };

  const children = [
    <ToggleButton
      value={ShippingStatus.CANCEL_REQUESTED}
      key={ShippingStatus.CANCEL_REQUESTED}
    >
      <OrderStatus
        status={ShippingStatus.CANCEL_REQUESTED}
        isAdmin={true}
        displayChipForZeroCount
        disabled={
          orderItemAction.shippingStatus !== ShippingStatus.CANCEL_REQUESTED
        }
        styles={effectiveStyles}
      />
    </ToggleButton>,
    <ToggleButton
      value={ShippingStatus.CANCELLED}
      key={ShippingStatus.CANCELLED}
    >
      <OrderStatus
        status={ShippingStatus.CANCELLED}
        isAdmin={true}
        label="Process Cancel"
        displayChipForZeroCount
        disabled={orderItemAction.shippingStatus !== ShippingStatus.CANCELLED}
        styles={effectiveStyles}
      />
    </ToggleButton>,
  ];

  const control = {
    value: orderItemAction.shippingStatus,
    onChange: handleChange,
    exclusive: true,
  };

  return (
    <>
      <Grid sx={effectiveStyles.OrderActionContainer}>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ToggleButtonGroup size="small" {...control} aria-label="Small sizes">
            {children}
          </ToggleButtonGroup>
        </Stack>
        {orderItemAction.shippingStatus !== ShippingStatus.CANCEL_REQUESTED && (
          <>
            <Grid
              className="OrderActionSingleItemTopPaddingRowContainer"
              sx={effectiveStyles.OrderActionSingleItemTopPaddingRowContainer}
            >
              <Grid
                className="OrderActionContentFullWidthTextFieldContainer"
                sx={
                  effectiveStyles.OrderActionContentFullWidthTextFieldContainer
                }
              >
                <TextField
                  className="OrderActionContentFullWidthTextField"
                  sx={effectiveStyles.OrderActionContentFullWidthTextField}
                  id="note"
                  label="Note"
                  value={orderItemAction.note || ""}
                  onChange={handleNoteValueChange}
                />
              </Grid>
            </Grid>
            <Grid
              className="OrderActionSingleItemRowContainer"
              sx={effectiveStyles.OrderActionSingleItemRowContainer}
            >
              <Grid
                className="OrderActionContentFullWidthTextFieldContainer"
                sx={
                  effectiveStyles.OrderActionContentFullWidthTextFieldContainer
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={orderItemAction.issueRefund}
                      onChange={handleIssueRefundValueChange}
                    />
                  }
                  label="Issue Item Refund"
                />
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default OrderCancellationRequestedActions;
